import './index.scss';
import React, { useMemo, useState } from 'react';
import { IMicroPageAddProps, IMicroPageInfo } from './interface';
import { Modal, Button, SnackBar } from '@umu/umu-design';
import { lang } from '@umu/ukit-lang';
import { If } from 'common/src/components';
import { MicroPageType } from '@umu/umu-types';
import { defaultInfo } from './dataFlow';
import { ModalFuncProps } from '@umu/design-base';
import { BasicInfo } from './basicInfo';
import { DEFAULT_BASIC_INFO } from './dataFlow';
import { IAddMicroParams, onSaveMicroPageBasicInfoSetting } from '@umu/umu-api';
import { MicroTemplateList } from './microTemplateList';

const { confirm } = Modal;
/**
 * @desc 微页面配置信息弹窗页面
 */
const AddMicroDialogPage = (props: IMicroPageAddProps) => {
    const {
        prefixCls = 'add-micro-page-dialog-info',
        className = '',
        pageType = MicroPageType.ADD,
        isHideFooter = false,
        info = defaultInfo,
        okText,
        cancelText,
        okProps,
        onUpdate,
        onSave,
        onClose,
        onSuccess,
        ...rest
    } = props;
    const [isSave, setIsSave] = useState(false);
    const [microInfo, setMicroInfo] = useState<IMicroPageInfo>(info);

    // 保存按钮文字信息
    const okTextName = useMemo(() => {
        if (okText) {
            return okText;
        }
        return pageType === MicroPageType.EDITOR ? lang('保存') : lang('下一步');
    }, [pageType, okText]);
    // 取消按钮文字
    const cancelTextName = useMemo(() => {
        return cancelText || lang('取消');
    }, [cancelText]);
    /**
     * @desc 保存配置信息
     */
    const onSaveMicroPage = (params: IAddMicroParams, pageType: MicroPageType) => {
        onSaveMicroPageBasicInfoSetting(params, pageType).then((res) => {
            setIsSave(false);
            if (res.page_id === -1) {
                SnackBar.show({
                    message: lang('配置下发失败'),
                });
                return;
            }
            onClose && onClose();
            onSuccess &&
                onSuccess({
                    isSuccess: true,
                    info: {
                        ...microInfo,
                        share_url: res.share_url,
                        id: String(res.page_id),
                    },
                });
        });
    };
    // 保存按钮属性
    const onSubmit = () => {
        if (isSave) {
            return;
        }
        setIsSave(true);
        const params: IAddMicroParams = {
            title: (microInfo?.title || '').trim(),
            desc: (microInfo?.desc || '').trim(),
        };
        const isHasTemplate = microInfo.template_id && microInfo.template_id !== '';
        if (isHasTemplate) {
            params.template_id = microInfo.template_id;
        }
        if (pageType === MicroPageType.EDITOR) {
            params.page_id = microInfo.id;
        } else if (pageType === MicroPageType.COPY) {
            params.from_page_id = microInfo.id;
        } else {
            // 新增
            params.setup_json = isHasTemplate ? '{}' : DEFAULT_BASIC_INFO;
        }
        if (onSave) {
            onSave(microInfo, {
                cancelLoading: () => setIsSave(false),
                onInfoChange: (val: IMicroPageInfo) => setMicroInfo(val),
                onSaveMicroPage: (val?: IAddMicroParams) => onSaveMicroPage(val || params, pageType),
            });
        } else {
            // 页面默认处理函数
            onSaveMicroPage(params, pageType);
        }
    };
    // 是否显示模板信息
    const isShowTemplate = useMemo(() => {
        return [MicroPageType.ADD].includes(pageType);
    }, [pageType]);
    const onInfoChange = (data: IMicroPageInfo) => {
        const newInfo = {
            ...microInfo,
            ...data,
        };
        setMicroInfo(newInfo);
        onUpdate && onUpdate(newInfo);
    };
    const onSelectedTemplate = (id: string) => {
        onInfoChange({
            template_id: id,
        } as IMicroPageInfo);
    };
    return (
        <div className={`${prefixCls} ${className}`} {...rest}>
            <div className={`${prefixCls}-content`}>
                <BasicInfo
                    className={`${prefixCls}-content-basic-info`}
                    isValid={isSave}
                    info={microInfo}
                    onInfoChange={onInfoChange}
                />
                <If condition={isShowTemplate}>
                    <MicroTemplateList
                        className={`${prefixCls}-content-template-info`}
                        selectId={microInfo.template_id}
                        onSelected={onSelectedTemplate}
                        header={lang('请选择模板或直接开始设计')}
                    />
                </If>
            </div>
            <If condition={!isHideFooter}>
                <div className={`${prefixCls}-footer`}>
                    <Button type="ghost-grey" className={`${prefixCls}-footer-col-split`} onClick={onClose}>
                        {cancelTextName}
                    </Button>
                    <Button
                        type="primary-blue"
                        onClick={onSubmit}
                        disabled={isSave || (microInfo?.title || '').trim() === ''}
                        {...okProps}
                    >
                        {okTextName}
                    </Button>
                </div>
            </If>
        </div>
    );
};
/**
 * @desc 微页面配置信息弹窗页面 打开弹窗函数
 */
const onDialogAddMicroDialogPage = (data?: IMicroPageAddProps, dialogInfo?: ModalFuncProps) => {
    const { title = lang('基本信息'), ...rest } = dialogInfo || {};
    const dialog = confirm({
        title,
        centered: true,
        width: '8.6rem',
        footer: null,
        ...rest,
        content: (
            <AddMicroDialogPage
                {...data}
                onClose={() => {
                    dialog.destroy();
                    data && data.onClose && data.onClose();
                }}
            />
        ),
    } as any);
    return dialog;
};
export { onDialogAddMicroDialogPage, AddMicroDialogPage };
